import React, { useEffect, useState } from "react";
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";

/**
 * CertificateNameModal — one-time capture of the founder's display
 * name for every certificate they will ever download. The name is
 * saved server-side on `users.certificate_display_name` and cannot be
 * changed once submitted (server enforces via 409 on second POST).
 *
 * Contract:
 *   - open: boolean — parent controls visibility
 *   - onClose: () => void
 *   - onSaved: (savedName: string) => void — fires after successful save
 *   - defaultName?: string — prefill from the current session (auth "name")
 */
export default function CertificateNameModal({ open, onClose, onSaved, defaultName = "" }) {
  const [name, setName] = useState(defaultName);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Refresh the prefill any time the modal opens so the auth-session
    // name lands in the box every time a founder starts the flow.
    if (open) {
      setName(defaultName);
      setConfirmed(false);
    }
  }, [open, defaultName]);

  if (!open) return null;

  const trimmed = name.trim();
  const canSave = confirmed && trimmed.length >= 2 && trimmed.length <= 80 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post("/certifications/display-name", {
        display_name: trimmed,
        confirmed:    true,
      });
      toast.success("Name saved — downloading your certificate…");
      onSaved?.(trimmed);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center bg-black/50 backdrop-blur-sm px-4"
      data-testid="cert-name-modal"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-2xl leading-tight">Name your certificate</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                This is the name that will appear on every certificate you download
                — Certified CPG Founder™, AI for CPG Certification™, and every
                specialist credential you earn.
              </p>
            </div>
          </div>

          <label className="mt-6 block text-xs uppercase tracking-widest font-bold text-muted-foreground">
            Full name for your certificates
          </label>
          <input
            data-testid="cert-name-input"
            type="text"
            value={name}
            maxLength={80}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Nathan Ezra"
            className="mt-2 w-full rounded-xl border border-shell-border px-4 py-3 text-base focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
          />

          <div className="mt-4 rounded-xl border border-pink-200 bg-pink-50 p-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-pink-700 shrink-0 mt-0.5" />
            <p className="text-xs text-pink-900 leading-relaxed">
              <strong>Permanent choice.</strong> Once saved, this name cannot be
              changed. It will be used on every certificate you ever download
              from CPG Institute, and it appears on every LinkedIn verify page.
            </p>
          </div>

          <label className="mt-4 flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="cert-name-confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-primary"
            />
            <span className="text-sm text-shell-foreground">
              I understand this name is permanent and cannot be changed after saving.
            </span>
          </label>
        </div>

        <div className="border-t border-shell-border bg-shell-muted/40 px-6 py-4 flex items-center justify-end gap-2">
          <button
            data-testid="cert-name-cancel"
            onClick={onClose}
            disabled={saving}
            className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-shell-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            data-testid="cert-name-save"
            onClick={save}
            disabled={!canSave}
            className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Lock className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save & download"}
          </button>
        </div>
      </div>
    </div>
  );
}
